import uniqBy from 'lodash/uniqBy';
import uniq from 'lodash/uniq';

import { useQueryNoFurtherUpdateInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryNoFurtherUpdateInterop.ts';
import { recordNonContentAppKeys } from '@confluence/browser-metrics/entry-points/NonContentAppKeys.ts';
import { fg } from '@confluence/feature-gating';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

import {
	WebItemLocationQuery,
	type WebItemLocationQuery_webItemSections,
	type WebItemLocationQuery_webItemSections_items,
} from './WebItemLocation.tsx';
import type {
	WebItemLocationQuery$data,
	WebItemLocationQuery$variables,
} from './__generated__/WebItemLocationQuery.graphql.ts';
import { replaceAtlTokenPlaceholder } from './replaceAtlTokenPlaceholder.ts';

export type WebItem = Omit<WebItemLocationQuery_webItemSections_items, 'params'> & {
	parentSection?: {
		label?: string | null;
	};
	params?: Record<string, string>;
	webSection?: string;
};

export type UseWebItemLocationOptions = {
	// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
	location: string | string[];
	contentId?: string | null;
	spaceKey?: string | null;
	version?: number | null;
	allowedWebItems?: string[];
	notAllowedWebItems?: string[];
	skipQuery?: boolean;
	skipFetchIfCached?: boolean;
};

export const useWebItemLocation = ({
	location,
	contentId = null,
	spaceKey = null,
	version = null,
	allowedWebItems,
	notAllowedWebItems,
	skipQuery,
	skipFetchIfCached,
}: UseWebItemLocationOptions): {
	// Since the default fetchPolicy is cache-and-network
	// loading field will be true, false, true (background refetch), false
	// Query is refreshing when data not empty but loading is true
	// Padding loading false to avoid reverting back to the empty state or loading skeleton.
	// This aligns with confluence/next/packages/web-item-location/src/WebItemLocation.tsx
	loading: boolean;
	error: ApolloErrorInterop | undefined;
	webItems: WebItem[];
	webSections: (string | null)[];
} => {
	const baseQueryOptions = {
		variables: {
			// GraphQL doesn't cache single location strings properly when passed to locations
			// unless we're using multiple locations we should be using the location variable
			location: Array.isArray(location) ? null : location,
			locations: Array.isArray(location) ? location : [],
			contentId,
			spaceKey,
			version,
		},
		skip: skipQuery,
		skipFetchIfCached,
	};

	const { data, loading, error } = useQueryNoFurtherUpdateInterop<
		WebItemLocationQuery$data,
		WebItemLocationQuery$variables
	>(WebItemLocationQuery, baseQueryOptions);

	if (!data) {
		return {
			loading,
			error,
			webItems: [],
			webSections: [],
		};
	}

	// replace any instances of {atlTokenPlaceholderWillBeReplacedInFrontend}
	// with the atlToken in all section items
	const webItems = getWebItems(
		(replaceAtlTokenPlaceholder(data)?.webItemSections ??
			[]) as WebItemLocationQuery_webItemSections[],
		{
			allowedWebItems,
			notAllowedWebItems,
		},
	);

	const webSections = uniq(webItems.map((webItem) => webItem.section));

	webItems.sort(sortWebItems);

	// Instrument the presence of non-content 3P connect apps so their keys are
	// folded into the page-load `connectAppKeys` telemetry.
	if (fg('cc_record_non_content_macros')) {
		recordNonContentAppKeys(webItems.map((webItem) => webItem.completeKey));
	}

	return {
		// Since the default fetchPolicy is cache-and-network
		// loading field will be true, false, true (background refetch), false
		// Query is refreshing when data not empty but loading is true
		// Padding loading false to avoid reverting back to the empty state or loading skeleton.
		// This aligns with confluence/next/packages/web-item-location/src/WebItemLocation.tsx
		loading: false,
		error,
		webItems,
		webSections,
	};
};

const getWebItems = (
	webItemSections: (WebItemLocationQuery_webItemSections | null)[],
	webItemFilterOptions: WebItemFilterOptions,
): WebItem[] => {
	const webItems = webItemSections.flatMap((section) => {
		return (section!.items as WebItemLocationQuery_webItemSections_items[])
			.filter(filterWebItem(webItemFilterOptions))
			.map(transformWebItem(section!));
	});

	return uniqBy(webItems, 'completeKey');
};

type WebItemFilterOptions = {
	allowedWebItems?: string[];
	notAllowedWebItems?: string[];
};

const filterWebItem =
	({ allowedWebItems, notAllowedWebItems }: WebItemFilterOptions) =>
	(
		item: WebItemLocationQuery_webItemSections_items,
	): item is WebItemLocationQuery_webItemSections_items => {
		if (!item || !item.completeKey) {
			return false;
		}

		const isAllowed = !allowedWebItems || allowedWebItems.includes(item.completeKey);
		const isDisallowed = notAllowedWebItems?.includes(item.completeKey);

		return isAllowed && !isDisallowed;
	};

const transformWebItem =
	(section: WebItemLocationQuery_webItemSections) =>
	(rawItem: WebItemLocationQuery_webItemSections_items): WebItem => ({
		...rawItem,
		params: getParamsObject(rawItem),
		webSection: getWebSection(rawItem),
		parentSection: {
			label: section.label || section.styleClass,
		},
	});

const sortWebItems = (webItemA: WebItem, webItemB: WebItem) => {
	if (!webItemA.weight || !webItemB.weight || !webItemA.label || !webItemB.label) {
		return 0;
	}

	if (webItemA.weight - webItemB.weight === 0) {
		return webItemA.label.localeCompare(webItemB.label);
	} else {
		return webItemA.weight - webItemB.weight;
	}
};

const getWebSection = (item: WebItemLocationQuery_webItemSections_items) => {
	if (item.section && item.section.indexOf('/') !== -1) {
		const parts = item.section.split('/');
		return parts[1];
	}
};

const getParamsObject = (item: WebItemLocationQuery_webItemSections_items) => {
	type ParamsObjType = {
		[key: string]: string;
	};
	const paramObj: ParamsObjType = {};
	item.params?.forEach((param) => {
		if (param?.key) {
			paramObj[param.key] = param.value ?? '';
		}
	});
	return paramObj;
};
